import type { MigrationConfigVariablePlan, MigrationPlanDTO } from '@devrobot/shared';
import { Database, FileText, HardDrive, Info, SlidersHorizontal } from 'lucide-react';
import { IconChip } from '../../../components/PageHeader';
import { SkeletonLines } from '../../../components/Skeleton';
import { formatBytes } from '../../../lib/format';
import { kindOf, type StepProps, type WizardAction, type WizardState } from './model';

const HEAD = 'text-xs tracking-wide text-base-content/50 uppercase';

const DEST_BADGE = {
  missing: { cls: 'badge-ghost', label: 'missing' },
  same: { cls: 'badge-soft badge-success', label: 'same' },
  differs: { cls: 'badge-soft badge-warning', label: 'differs' },
} as const;

function DestBadge({ value }: { value: keyof typeof DEST_BADGE }) {
  const b = DEST_BADGE[value];
  return <span className={`badge badge-xs whitespace-nowrap ${b.cls}`}>{b.label}</span>;
}

function Heading({ icon, title, count, total }: { icon: typeof Database; title: string; count: number; total: number }) {
  return (
    <h3 className="mb-2 flex items-center gap-2 font-semibold">
      <IconChip icon={icon} tone="secondary" size="sm" /> {title}
      <span className="text-sm font-normal text-base-content/55">
        {count} of {total} selected
      </span>
    </h3>
  );
}

/** Variables table: include + overwrite checkboxes (overwrite only when the value differs and the strategy allows it). */
export function VariablesTable({ plan, state, dispatch }: { plan: MigrationPlanDTO; state: WizardState; dispatch: (a: WizardAction) => void }) {
  const vars = plan.config.variables;
  const overwriteAllowed = state.configConflict === 'overwrite-selected';
  const row = (v: MigrationConfigVariablePlan) => {
    const included = state.include.config.variables.includes(v.name);
    const canOverwrite = overwriteAllowed && v.destination === 'differs' && included;
    return { included, canOverwrite, overwrite: state.overwriteVariables.includes(v.name) };
  };
  if (vars.length === 0) return <p className="text-sm text-base-content/60">No configuration variables to copy.</p>;
  return (
    <div className="overflow-x-auto rounded-field border border-hairline">
      <table className="table table-sm">
        <thead>
          <tr className={HEAD}>
            <th>Copy</th>
            <th>Variable</th>
            <th className="max-sm:hidden">Layer</th>
            <th>At destination</th>
            <th>Overwrite</th>
          </tr>
        </thead>
        <tbody>
          {vars.map((v) => {
            const r = row(v);
            return (
              <tr key={v.name} className={r.included ? '' : 'text-base-content/50'}>
                <td>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    aria-label={`Copy ${v.name}`}
                    checked={r.included}
                    onChange={(e) => dispatch({ type: 'toggle', field: 'variables', value: v.name, on: e.target.checked })}
                  />
                </td>
                <td>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <code className="font-mono text-sm break-anywhere">{v.name}</code>
                    {v.secret ? <span className="badge badge-soft badge-warning badge-xs">secret</span> : null}
                    {v.kind === 'binding' ? <span className="badge badge-soft badge-info badge-xs">binding</span> : null}
                    {!v.declared ? <span className="badge badge-ghost badge-xs">undeclared</span> : null}
                  </div>
                </td>
                <td className="text-xs text-base-content/65 max-sm:hidden">{v.layer}</td>
                <td>
                  <DestBadge value={v.destination} />
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-warning"
                    aria-label={`Overwrite ${v.name} at the destination`}
                    disabled={!r.canOverwrite}
                    checked={r.canOverwrite && r.overwrite}
                    onChange={(e) => dispatch({ type: 'toggle', field: 'overwriteVariables', value: v.name, on: e.target.checked })}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ScopeStep({ state, dispatch, preview }: StepProps) {
  const plan = preview.plan;
  if (!plan) return <SkeletonLines lines={8} />;
  const serverMove = kindOf(state) === 'server-move';
  const inc = state.include;
  const vpsOverrides = plan.config.overrides.filter((o) => o.scope === 'vps');
  const targetOverrides = plan.config.overrides.filter((o) => o.scope === 'target');

  return (
    <div className="space-y-7">
      <section>
        <Heading icon={Database} title="Databases" count={inc.databases.length} total={plan.databases.length} />
        {plan.databases.length === 0 ? <p className="text-sm text-base-content/60">No databases are declared.</p> : null}
        <ul className="space-y-2">
          {plan.databases.map((d) => (
            <li key={d.service}>
              <label className="flex cursor-pointer items-start gap-3 rounded-field bg-base-200/60 p-3">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm mt-0.5"
                  checked={inc.databases.includes(d.service)}
                  onChange={(e) => dispatch({ type: 'toggle', field: 'databases', value: d.service, on: e.target.checked })}
                />
                <span className="min-w-0 flex-1 space-y-1 text-sm">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-medium">{d.service}</span>
                    <span className="text-base-content/60">({d.type})</span>
                    <span className={`badge badge-xs ${d.strategy === 'logical-dump' ? 'badge-soft badge-info' : 'badge-soft badge-warning'}`}>{d.strategy}</span>
                    {d.destinationHasData ? <span className="badge badge-soft badge-error badge-xs">destination has data</span> : null}
                  </span>
                  <span className="block text-xs text-base-content/60">
                    volume <span className="font-mono">{d.volume}</span>
                    {d.stopsServices.length ? ` · stops ${d.stopsServices.join(', ')} while copying` : ' · no service is stopped'}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <Heading icon={HardDrive} title="Volumes" count={inc.volumes.length} total={plan.volumes.length} />
        {plan.volumes.length === 0 ? <p className="text-sm text-base-content/60">No other volumes are declared.</p> : null}
        <ul className="space-y-2">
          {plan.volumes.map((v) => (
            <li key={v.name}>
              <label className="flex cursor-pointer items-start gap-3 rounded-field bg-base-200/60 p-3">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm mt-0.5"
                  checked={inc.volumes.includes(v.name)}
                  onChange={(e) => dispatch({ type: 'toggle', field: 'volumes', value: v.name, on: e.target.checked })}
                />
                <span className="min-w-0 flex-1 space-y-1 text-sm">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-medium break-anywhere">{v.name}</span>
                    <span className="badge badge-soft badge-warning badge-xs">{v.strategy}</span>
                    {v.sizeBytes !== undefined ? <span className="text-xs text-base-content/60 tabular-nums">{formatBytes(v.sizeBytes)}</span> : null}
                    {v.destinationHasData ? <span className="badge badge-soft badge-error badge-xs">destination has data</span> : null}
                  </span>
                  <span className="block text-xs text-base-content/60">{v.stopsServices.length ? `Stops ${v.stopsServices.join(', ')} while copying` : 'No service mounts it'}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <Heading icon={SlidersHorizontal} title="Configuration" count={inc.config.variables.length} total={plan.config.variables.length} />
        {serverMove || !plan.config.copiesEnvironmentValues ? (
          <div role="note" className="alert alert-info alert-soft text-sm">
            <Info className="size-4 shrink-0" aria-hidden="true" />
            <span>Environment-level values are already shared by every VPS of {state.destEnvironment}; only VPS- and target-specific values are copied.</span>
          </div>
        ) : null}
        <fieldset className="flex flex-col gap-2 sm:flex-row sm:gap-6" role="radiogroup" aria-label="When a value already exists at the destination">
          <legend className="mb-1 text-sm font-medium">When a value already exists at the destination</legend>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input type="radio" className="radio radio-sm" name="config-conflict" checked={state.configConflict === 'keep-destination'} onChange={() => dispatch({ type: 'set', patch: { configConflict: 'keep-destination' } })} />
            Keep the destination value
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input type="radio" className="radio radio-sm" name="config-conflict" checked={state.configConflict === 'overwrite-selected'} onChange={() => dispatch({ type: 'set', patch: { configConflict: 'overwrite-selected' } })} />
            Overwrite the ones I select
          </label>
        </fieldset>
        <VariablesTable plan={plan} state={state} dispatch={dispatch} />

        {plan.config.files.length > 0 ? (
          <div>
            <h4 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
              <FileText className="size-4" aria-hidden="true" /> Private files
            </h4>
            <ul className="space-y-1.5">
              {plan.config.files.map((f) => (
                <li key={f.name}>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={inc.config.files.includes(f.name)}
                      onChange={(e) => dispatch({ type: 'toggle', field: 'files', value: f.name, on: e.target.checked })}
                    />
                    <span className="font-mono break-anywhere">{f.name}</span>
                    <DestBadge value={f.destination} />
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="space-y-2">
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input type="checkbox" className="toggle toggle-sm mt-0.5" checked={inc.config.vpsOverrides} onChange={(e) => dispatch({ type: 'set-overrides', scope: 'vpsOverrides', on: e.target.checked })} />
            <span>
              Copy VPS-specific overrides
              <span className="block text-xs text-base-content/60">
                {vpsOverrides.length ? vpsOverrides.map((o) => `${o.name} → ${o.to}`).join(', ') : 'None are set.'}
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input type="checkbox" className="toggle toggle-sm mt-0.5" checked={inc.config.targetOverrides} onChange={(e) => dispatch({ type: 'set-overrides', scope: 'targetOverrides', on: e.target.checked })} />
            <span>
              Copy target-specific overrides
              <span className="block text-xs text-base-content/60">
                {targetOverrides.length ? targetOverrides.map((o) => `${o.name} → ${o.to}`).join(', ') : 'None are set.'}
              </span>
            </span>
          </label>
        </div>

        {plan.config.notCopied.length > 0 ? (
          <div role="note" className="alert alert-soft text-sm">
            <Info className="size-4 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-medium">Not copied</p>
              <ul className="list-disc pl-5">
                {plan.config.notCopied.map((n, i) => (
                  <li key={i} className="break-anywhere">
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
